// ABOUTME: One feeling's full 2A scene: its looping sky-and-sea art, the floating face and the feeling's name above it.
// ABOUTME: Drawn on the prototype's 850 x 370 stage, scaled to fit and anchored so the stage's horizon lands on the sea's horizon line.

import { Box, Typography } from '@mui/material';
import { CROSSING_HEADLINE_INK } from '../../../../theme/crossingTokens';
import type { CrossingFeelingDto } from '../../../../types/api';
import FeelingFaceRing from '../../crossing/FeelingFaceRing';
import { useHorizon } from '../../crossing/useHorizon';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import { useFrameScale } from '../../crossing/useFrameScale';
import { WR_RIDE_ANIMATION, WR_RIDE_KEYFRAMES } from '../../crossing/wrRide';
import { FACE_SIZE, FACE_TOP, NAME_HEIGHT, FEELING_PLATE_WIDTH, STAGE_HEIGHT, STAGE_HORIZON, STAGE_WIDTH } from './feelingCopyLayout';
import { FEELING_FIGURE, FEELING_KEYFRAMES, FEELING_SCENES, FEELING_SPRITE_PX, ScaredDim } from './feelingScenes';

// The scene paints behind the screen's own text and arrows (z -1 inside the screen layer, which is its own stacking context).

interface FeelingSceneProps {
  feeling: CrossingFeelingDto;
}

export default function FeelingScene({ feeling }: FeelingSceneProps) {
  const reducedMotion = useCrossingReducedMotion();
  const { frameSc } = useFrameScale(FEELING_PLATE_WIDTH);
  const { line } = useHorizon();
  const Body = Object.prototype.hasOwnProperty.call(FEELING_SCENES, feeling.code) ? FEELING_SCENES[feeling.code] : undefined;
  const figure = Object.prototype.hasOwnProperty.call(FEELING_FIGURE, feeling.code) ? FEELING_FIGURE[feeling.code] : undefined;
  const ride = reducedMotion ? { animation: 'none' } : { animation: WR_RIDE_ANIMATION };

  return (
    <>
      {feeling.code === 'scared' && (
        <Box aria-hidden="true" sx={{ position: 'fixed', left: 0, right: 0, top: 0, height: line, pointerEvents: 'none', zIndex: -1, ...FEELING_KEYFRAMES }}>
          <ScaredDim />
        </Box>
      )}
      <Box
        data-testid={`feeling-scene-${feeling.code}`}
        sx={{
          position: 'fixed', left: '50%', top: `${line - STAGE_HORIZON * frameSc}px`, width: STAGE_WIDTH, height: STAGE_HEIGHT,
          marginLeft: `${-STAGE_WIDTH / 2}px`, transform: `scale(${frameSc})`, transformOrigin: '50% 0', pointerEvents: 'none', zIndex: -1,
          ...FEELING_KEYFRAMES, ...WR_RIDE_KEYFRAMES,
        }}
      >
        {Body && figure && (
          <Box
            data-testid="feeling-art"
            sx={{ position: 'absolute', inset: 0, transform: `scale(${FEELING_SPRITE_PX / figure.width})`, transformOrigin: `${figure.cx}px ${figure.cy}px` }}
          >
            <Body />
          </Box>
        )}
        <Typography
          data-testid="feeling-scene-name"
          sx={{
            position: 'absolute', left: '50%', top: FACE_TOP - NAME_HEIGHT, width: 300, marginLeft: '-150px', textAlign: 'center',
            fontFamily: 'Newsreader, serif', fontSize: '44px', fontWeight: 600, lineHeight: `${NAME_HEIGHT}px`, color: CROSSING_HEADLINE_INK, ...ride,
          }}
        >
          {feeling.name}
        </Typography>
        <FeelingFaceRing
          code={feeling.code}
          role="img"
          aria-label={feeling.sentence}
          sx={{ position: 'absolute', left: '50%', top: FACE_TOP, marginLeft: `${-FACE_SIZE / 2}px`, ...ride }}
        />
      </Box>
    </>
  );
}
